import { Input } from "@medusajs/ui";

export interface NewPasswordFieldsProps {
  password: string;
  confirmation: string;
  onPasswordChange: (value: string) => void;
  onConfirmationChange: (value: string) => void;
  /** Placeholder and accessible name of the first field. */
  passwordLabel: string;
  /** Placeholder and accessible name of the confirm field. */
  confirmationLabel: string;
  /**
   * Announce both inputs as required (`aria-required`). On by default: every
   * screen that sets a password wants it. Deliberately not the native
   * `required` attribute: @medusajs/ui's Input paints the error ring on the CSS
   * `:invalid` state, which an empty required field is in from first paint. The
   * screen's own validation (`validateNewPassword`) is what blocks an empty
   * submit instead.
   */
  required?: boolean;
  /**
   * Show the error ring on both inputs. Set it from the screen's validation,
   * after a failed submit - never before the user has tried.
   */
  invalid?: boolean;
  /** Id of the element that explains the problem, wired to both inputs. */
  describedBy?: string;
}

/**
 * The "new password + confirm" pair, for the auth-style screens that sit on the
 * `bg-ui-bg-subtle` page (reset, activation).
 *
 * Controlled and stateless: the screen owns the values, validates them with
 * `validateNewPassword` (`@/lib/password`) and decides what happens on submit.
 * The inputs are never echoed anywhere and `maskAllInputs` already keeps their
 * values out of session replay (ADR-0006), so nothing here needs a mask.
 */
export function NewPasswordFields({
  password,
  confirmation,
  onPasswordChange,
  onConfirmationChange,
  passwordLabel,
  confirmationLabel,
  required = true,
  invalid = false,
  describedBy
}: NewPasswordFieldsProps) {
  return (
    <>
      <Input
        type="password"
        aria-required={required}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        autoComplete="new-password"
        placeholder={passwordLabel}
        value={password}
        onChange={(event) => onPasswordChange(event.target.value)}
        className="bg-ui-bg-field-component"
        aria-label={passwordLabel}
      />
      <Input
        type="password"
        aria-required={required}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        autoComplete="new-password"
        placeholder={confirmationLabel}
        value={confirmation}
        onChange={(event) => onConfirmationChange(event.target.value)}
        className="bg-ui-bg-field-component"
        aria-label={confirmationLabel}
      />
    </>
  );
}
